import { Button } from '@/components/common/Button';
import FadeInBox from '@/components/common/FadeInBox';
import { FlexCol, FlexRow } from '@/components/common/Flex';
import { LightBulb04 } from '@/components/common/icons';
import InputField from '@/components/common/InputField';
import { Header, Paragraph, Text } from '@/components/common/Typography';
import { useMutation } from '@apollo/client';
import { FlexProps } from '@chakra-ui/react';
import { assert } from '@common/errors';
import { UpdateGoalDocument } from '@common/graphql/generated';
import { isNumber } from '@common/util/types';
import { isValidDateString } from '@common/validation';
import { useCallback, useState } from 'react';
import {
    earliestWithdrawalOptions,
    getTargetDateFromYearMonth,
    getYearMonthFromTargetDate,
} from '../../helpers';
import { useGoalOnboardingContext } from './GoalOnboardingContext';
import { showGoalOnboardingGuidanceModal } from './GoalOnboardingGuidanceModal';
import GoalOnboardingLayout from './GoalOnboardingLayout';

export interface GoalOnboardingTargetProps extends FlexProps {
    onBack?: () => void;
    onComplete: () => void;
}

const GoalOnboardingTarget = ({
    onBack,
    onComplete,
    ...props
}: GoalOnboardingTargetProps) => {
    const { goal } = useGoalOnboardingContext();
    return goal.type === 'GENERAL_INVESTING' ? (
        <EarliestWithdrawalLayout
            onBack={onBack}
            onComplete={onComplete}
            {...props}
        />
    ) : (
        <TargetAmountAndTargetDateLayout
            onBack={onBack}
            onComplete={onComplete}
            {...props}
        />
    );
};

const TargetAmountAndTargetDateLayout = ({
    onBack,
    onComplete,
    ...props
}: GoalOnboardingTargetProps) => {
    const { goal, updateGoalOnboarding, isUpdating } =
        useGoalOnboardingContext();
    const [updateGoal] = useMutation(UpdateGoalDocument);
    const [targetAmount, setTargetAmount] = useState(
        goal.targetAmount ?? undefined
    );
    const [yearMonth, setYearMonth] = useState(
        isValidDateString(goal.targetDate)
            ? getYearMonthFromTargetDate(goal.targetDate)
            : undefined
    );

    const onFinished = useCallback(
        async (targetDate: string | null | undefined) => {
            await updateGoalOnboarding({
                params: {
                    info: {
                        lastCompletedStep: 'TARGET',
                        // If target date is not specified,
                        // use a risk score of 0
                        ...(!targetDate ? { riskScore: 0 } : {}),
                    },
                },
                // Reset all onboarding state to ensure new recommended
                // risk score gets picked up.
                resetData: true,
            });
            onComplete();
        },
        [onComplete, updateGoalOnboarding]
    );

    return (
        <GoalOnboardingLayout
            {...props}
            footer={{
                back: onBack ? { onBack } : undefined,
                cta: {
                    onComplete: async () => {
                        const targetDate =
                            yearMonth?.length === 6
                                ? getTargetDateFromYearMonth(yearMonth)
                                : undefined;

                        if (isNumber(targetAmount) || targetDate) {
                            await updateGoal({
                                variables: {
                                    id: goal.id,
                                    targetAmount: isNumber(targetAmount)
                                        ? targetAmount
                                        : undefined,
                                    targetDate,
                                },
                            });
                        }

                        await onFinished(targetDate);
                    },
                    isSubmitting: isUpdating,
                },
            }}
        >
            <FadeInBox>
                <FlexRow
                    w="full"
                    gap={8}
                    justifyContent="space-between"
                    alignItems="flex-start"
                >
                    <FlexCol>
                        <Header mb={10}>
                            Do you have a target $ amount and date in mind?
                        </Header>
                        <FlexRow gap={6}>
                            <InputField
                                id="targetAmount"
                                name="targetAmount"
                                type="currency"
                                value={targetAmount}
                                onChange={setTargetAmount}
                                placeholder="Enter an amount"
                                inputOptions={{ min: 1, preserveEmpty: true }}
                                inputProps={{ size: 'xl' }}
                            />
                            <Text fontSize="xl" mt={2}>
                                by
                            </Text>
                            <InputField
                                id="yearMonth"
                                name="yearMonth"
                                type="yearMonth"
                                inputProps={{ size: 'xl' }}
                                value={yearMonth}
                                onChange={setYearMonth}
                            />
                        </FlexRow>
                    </FlexCol>
                    <FlexCol
                        maxW={220}
                        justifyContent="flex-start"
                        // Avoid showing goal guidance for Custom goals,
                        // but preserve width to prevent inputs from stretching
                        visibility={
                            goal.type === 'CUSTOM' ? 'hidden' : 'visible'
                        }
                    >
                        <LightBulb04 color="textMuted" mb={3} />
                        <Paragraph
                            fontSize="sm"
                            color="textMuted"
                            lineHeight="taller"
                            mb={4}
                        >
                            If you’re not sure, we can help you come up with a
                            reasonable amount and date that works for you.
                        </Paragraph>
                        <FlexRow>
                            <Button
                                variant="outline"
                                onClick={() => {
                                    showGoalOnboardingGuidanceModal({
                                        goal,
                                        onComplete: async (goal) => {
                                            setTargetAmount(
                                                goal.targetAmount ?? undefined
                                            );
                                            setYearMonth(
                                                goal.targetDate
                                                    ? getYearMonthFromTargetDate(
                                                          goal.targetDate
                                                      )
                                                    : undefined
                                            );
                                            await onFinished(goal.targetDate);
                                        },
                                    });
                                }}
                                onClickEvent="Goal Guidance Button Clicked"
                                onClickEventParams={{
                                    goal_type: goal.type,
                                }}
                                mt={4}
                            >
                                Goal guidance
                            </Button>
                        </FlexRow>
                    </FlexCol>
                </FlexRow>
            </FadeInBox>
        </GoalOnboardingLayout>
    );
};

const EarliestWithdrawalLayout = ({
    onBack,
    onComplete,
    ...props
}: GoalOnboardingTargetProps) => {
    const { goal, updateGoalOnboarding, isUpdating } =
        useGoalOnboardingContext();
    assert(goal, 'Missing goal');
    assert(goal.type === 'GENERAL_INVESTING', 'Expected GENERAL_INVESTING');
    const [updateGoal] = useMutation(UpdateGoalDocument);
    const [earliestWithdrawal, setEarliestWithdrawal] = useState(
        goal.typeInfo.earliestWithdrawal ?? undefined
    );

    return (
        <GoalOnboardingLayout
            {...props}
            footer={{
                back: onBack ? { onBack } : undefined,
                cta: {
                    onComplete: async () => {
                        if (!earliestWithdrawal) return;
                        await updateGoal({
                            variables: {
                                id: goal.id,
                                typeInfo: {
                                    ...goal.typeInfo,
                                    earliestWithdrawal,
                                },
                            },
                        });
                        await updateGoalOnboarding({
                            params: {
                                info: {
                                    lastCompletedStep: 'TARGET',
                                },
                            },
                            // Reset all onboarding state to ensure new
                            // recommended risk score gets picked up.
                            resetData: true,
                        });
                        onComplete();
                    },
                    isSubmitting: isUpdating,
                },
            }}
        >
            <FadeInBox>
                <Header mb={10}>
                    What is the earliest you may want to withdraw these funds?
                </Header>
                <FlexRow alignItems="flex-end" gap={4}>
                    <InputField
                        name="radioCards"
                        type="radioCards"
                        radioProps={{ size: 'xl' }}
                        value={earliestWithdrawal}
                        onChange={setEarliestWithdrawal}
                        options={earliestWithdrawalOptions}
                    />
                </FlexRow>
            </FadeInBox>
        </GoalOnboardingLayout>
    );
};

export default GoalOnboardingTarget;
